import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { actions } from "core/constants";

const {
  GET_GAME_HISTORY,
  GAME_HISTORY_UPDATE,
  PLAYER_ADDED,
  UPDATED_SESSION_GAME_STATE,
  UPDATED_GAME_SETTINGS,
  ENDED,
} = actions;

const HistoryContainer = styled.div`
  padding: 1rem;
  background: #181818;
  color: #fff;
  min-height: 100vh;
`;

const EventBlock = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #232323;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EventTime = styled.div`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 0.5rem;
`;

const formatEvent = (event) => {
  // Simple formatter, can be improved for each action type
  return (
    <>
      <strong>{event.action}</strong>
      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(event.payload, null, 2)}
      </pre>
    </>
  );
};

export default function GameHistoryTimeline({ socket, gameSessionId }) {
  const [eventLog, setEventLog] = useState([]);
  const [seenEventIds, setSeenEventIds] = useState(new Set());

  useEffect(() => {
    if (!socket || !gameSessionId) return;

    // Fetch initial history on mount
    socket.emit(GET_GAME_HISTORY, { gameSessionId }, (response) => {
      if (response.status === 200) {
        const events = response.data.eventLog || [];
        setEventLog(events);
        // Track all event IDs from history to prevent duplicates
        const ids = new Set(events.map((e) => e.id).filter(Boolean));
        setSeenEventIds(ids);
      } else {
        console.error("Failed to fetch game history:", response.error);
      }
    });

    // Listen for full event log updates
    const onHistory = (data) => {
      const events = data.eventLog || [];
      setEventLog(events);
      // Update seen IDs when full history is replaced
      const ids = new Set(events.map((e) => e.id).filter(Boolean));
      setSeenEventIds(ids);
    };
    socket.on(GAME_HISTORY_UPDATE, onHistory);

    // Listen for all public events and append them to the log
    const appendEvent = (action) => (payload) => {
      const newEvent = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        action,
        payload,
        timestamp: new Date().toISOString(),
      };

      // Deduplicate: only add if we haven't seen this event ID
      setEventLog((prev) => {
        // Check if event already exists (shouldn't happen with unique IDs, but safe)
        if (seenEventIds.has(newEvent.id)) {
          console.log(`⚠️ Duplicate event detected, skipping: ${newEvent.id}`);
          return prev;
        }
        return [...prev, newEvent];
      });

      // Track this event ID
      setSeenEventIds((prev) => new Set([...prev, newEvent.id]));
    };

    // List of public events to listen for
    const publicEvents = [
      `${gameSessionId}-${PLAYER_ADDED}`,
      `${gameSessionId}-${UPDATED_SESSION_GAME_STATE}`,
      `${gameSessionId}-${UPDATED_GAME_SETTINGS}`,
      `${gameSessionId}-${ENDED}`,
    ];

    // Register listeners for each public event
    const listeners = {};
    publicEvents.forEach((event) => {
      const handler = appendEvent(event);
      listeners[event] = handler;
      socket.on(event, handler);
    });

    // Cleanup
    return () => {
      socket.off(GAME_HISTORY_UPDATE, onHistory);
      Object.entries(listeners).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, gameSessionId, seenEventIds]);

  return (
    <HistoryContainer>
      <h2>Game History</h2>
      {eventLog.length === 0 && <div>No events yet.</div>}
      {eventLog.map((event, idx) => (
        <EventBlock key={idx}>
          <EventTime>{new Date(event.timestamp).toLocaleString()}</EventTime>
          {formatEvent(event)}
        </EventBlock>
      ))}
    </HistoryContainer>
  );
}
